import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight } from "lucide-react";
import { scrollToId } from "@/components/Navbar";

const LINES = [
  { text: "SEE IT.", cls: "text-white" },
  { text: "HEAR IT.", cls: "text-white" },
  { text: "FEEL IT.", cls: "text-[#2aa7ec]" },
];

const lineVariants = {
  hidden: { y: "112%" },
  show: (i) => ({
    y: "0%",
    transition: { duration: 1, ease: [0.22, 1, 0.36, 1], delay: 0.35 + i * 0.14 },
  }),
};

export default function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "18%"]);

  return (
    <section
      id="home"
      ref={ref}
      className="relative min-h-[92vh] bg-[#0a1f33] overflow-hidden flex items-center"
      data-testid="hero-section"
    >
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_70%_30%,#123453_0%,#0a1f33_60%)]" />

      <div className="relative z-10 mx-auto max-w-7xl px-5 sm:px-8 pt-32 pb-20 grid lg:grid-cols-2 gap-10 items-center w-full">
        <div>
          <motion.img
            src="/verixx-logo.png"
            alt="Verix H₂O — A LeakLock Company"
            initial={{ opacity: 0, y: -14, filter: "blur(6px)" }}
            animate={{ opacity: 1, y: 0, filter: "blur(0px)" }}
            transition={{ duration: 1, ease: [0.22, 1, 0.36, 1], delay: 0.15 }}
            className="h-12 sm:h-14 w-auto mb-10 drop-shadow-[0_0_20px_rgba(28,156,232,0.4)]"
            data-testid="hero-brand-logo"
          />
          <h1 className="font-display font-black uppercase leading-[1.02] tracking-tight text-5xl sm:text-6xl lg:text-7xl">
            {LINES.map((line, i) => (
              <span key={line.text} className="block overflow-hidden pb-1">
                <motion.span
                  custom={i}
                  variants={lineVariants}
                  initial="hidden"
                  animate="show"
                  className={`block ${line.cls}`}
                  data-testid={`hero-headline-line-${i + 1}`}
                >
                  {line.text}
                </motion.span>
              </span>
            ))}
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 1 }}
            className="mt-7 font-display font-bold uppercase tracking-wide text-sm sm:text-base text-slate-300 max-w-md leading-relaxed"
            data-testid="hero-subheadline"
          >
            A smarter way to connect plumbing.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 1.2 }}
            className="mt-9"
          >
            <button
              onClick={() => scrollToId("contact")}
              className="group flex items-center gap-2.5 px-7 py-3.5 rounded-full bg-[#1c9ce8] hover:bg-[#3ab0f5] text-white text-sm font-semibold shadow-[0_8px_30px_rgba(28,156,232,0.35)] hover:shadow-[0_8px_38px_rgba(28,156,232,0.55)] transition-[background-color,box-shadow] duration-300"
              data-testid="hero-request-pilot-button"
            >
              Register Interest
              <ArrowRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-1" />
            </button>
          </motion.div>
        </div>

        <motion.div style={{ y: imgY }} className="relative">
          <motion.img
            src="/fitting-hero.png"
            alt="VERIXX H2O push-fit fitting with blue confirmation rings on pipe"
            initial={{ opacity: 0, x: 60, scale: 1.05 }}
            animate={{ opacity: 1, x: 0, scale: 1 }}
            transition={{ duration: 1.3, ease: [0.22, 1, 0.36, 1], delay: 0.5 }}
            className="w-full img-feather select-none"
            data-testid="hero-product-image"
          />
          <motion.p
            initial={{ opacity: 0, rotate: -4 }}
            animate={{ opacity: 1, rotate: -2 }}
            transition={{ duration: 1, delay: 1.6 }}
            className="mt-2 text-right font-script text-2xl sm:text-3xl text-slate-300"
            data-testid="hero-script-tagline"
          >
            Built for a stronger tomorrow.
          </motion.p>
        </motion.div>
      </div>
    </section>
  );
}
